import { ClientRect, DragOverlay, useDndMonitor } from "@dnd-kit/core";
import { useMemo, useState } from "react";
import { useDataTableSettings } from "../hooks/useDataTableSettings";
import DataTableColumnName from "./DataTableColumnName";

interface DataTableDraggedColumnOverlayProps {
  draggingColumnId: string | null;
}

const DataTableDraggedColumnOverlay = ({
  draggingColumnId,
}: DataTableDraggedColumnOverlayProps) => {
  const { table } = useDataTableSettings();

  const draggedColumn = useMemo(() => {
    if (!draggingColumnId) return null;
    return table.getColumn(draggingColumnId);
  }, [draggingColumnId, table]);

  const draggedHeader = useMemo(() => {
    if (!draggingColumnId) return null;
    return table
      .getHeaderGroups()
      .find((headerGroup) =>
        headerGroup.headers.some((header) => header.id === draggingColumnId),
      )
      ?.headers.find((header) => header.id === draggingColumnId);
  }, [draggingColumnId, table]);

  const [over, setOver] = useState<string | null>(null);
  const [overRect, setOverRect] = useState<ClientRect | null>(null);

  useDndMonitor({
    onDragMove: (event) => {
      setOver(event.over?.id as string);
      const rect = event.over?.rect;
      if (rect) {
        setOverRect(rect);
      }
    },
    onDragEnd: () => {
      setOver(null);
      setOverRect(null);
    },
  });

  const overStyle = useMemo(() => {
    const overRectLeft = overRect?.left ?? 0;
    const overRectWidth = overRect?.width ?? 0;
    const overRectTop = overRect?.top ?? 0;
    return {
      width: overRectWidth,
      left: overRectLeft,
      top: overRectTop,
    };
  }, [overRect]);

  return (
    <>
      <DragOverlay>
        {draggedColumn && draggedHeader && (
          <div className="w-full h-full bg-blue-400/40 p-2 backdrop-blur-[2px] cursor-grabbing">
            <div className="p-1">
              <DataTableColumnName
                header={draggedHeader}
                className="text-cyan-800 text-sm hover:bg-transparent"
              />
            </div>
          </div>
        )}
      </DragOverlay>
      {over && (
        <div
          className="absolute bg-gradient-to-b from-blue-300/30 via-blue-300/30 to-blue-300/0 pointer-events-none top-0 left-0 w-10 h-40 bottom-0 z-50"
          style={overStyle}
        />
      )}
    </>
  );
};

export default DataTableDraggedColumnOverlay;
